/*
 * Infinite scroll for collection pages (in-theme replacement for the Magepow "Infinite Scroll" app).
 *
 * Progressive enhancement on top of the server-rendered pagination: while JS is active the
 * pagination is hidden and next pages are fetched from their real ?page=N URLs through the
 * Section Rendering API, then appended into <product-list>. Crawlers and no-JS visitors keep
 * the untouched pagination links (they stay in the DOM, only visually hidden), so rel="next"
 * discovery is unaffected.
 *
 * ---------------------------------------------------------------------------------------------
 * Zurück-Taste / back button
 * ---------------------------------------------------------------------------------------------
 * An earlier version rewrote the address bar to ?page=N while scrolling (history.replaceState).
 * That broke the back button: after auto-loading a few pages the history entry pointed at
 * ?page=4, so going back from a product landed on a bare 8-product server page instead of the
 * list the visitor came from.
 *
 * The address bar is now never touched — it keeps the entry URL including any filter/sort
 * parameters. Instead the *scroll state* (how many pages were loaded, how far down the visitor
 * was) is written into history.state, which travels with the history entry itself. On back:
 *
 *   - bfcache hit  → the browser restores the full DOM and position, nothing to do.
 *   - bfcache miss → this element replays the missing pages and re-scrolls to the saved offset.
 *     (Third-party scripts such as Microsoft Clarity register unload handlers, which disables
 *     bfcache, so the miss path is the normal case here — not an edge case.)
 *
 * Everything is wrapped so that a failure degrades to the plain server pagination rather than
 * to an empty or frozen product list.
 */

// Key inside history.state. Namespaced because the theme's facet-form also writes history.state.
const STATE_KEY = "vivCollectionScroll";

// Restoring is capped so that a visitor who scrolled very far does not trigger a minute-long
// burst of requests on the way back. Beyond the cap they get as much as fits in the budget.
const MAX_RESTORE_PAGES = 60;
const RESTORE_BUDGET_MS = 30000;

// Replaying page by page would take a visitor who browsed deep into the collection far too
// long, so the restore fetches in small batches and appends them in order. Kept low on
// purpose — this is a burst against the shop's own section endpoint, not a crawl.
const RESTORE_BATCH = 6;

// Safari throws after ~100 history writes per 30 s, so scroll saves are throttled hard.
const SAVE_THROTTLE_MS = 500;

class InfiniteScroll extends HTMLElement {
  connectedCallback() {
    try {
      this._setup();
    } catch (error) {
      // Never let a scripting problem cost the shop its product list.
      this._degrade();
    }
  }

  disconnectedCallback() {
    this._teardown();
  }

  // ------------------------------------------------------------------------------------------
  // Setup
  // ------------------------------------------------------------------------------------------

  _setup() {
    // The Magepow app embed does the same job; bail out to avoid double loading while it is still enabled.
    if (document.getElementById("magepowapps-infinitescroll-settings")) {
      return;
    }

    const section = this.closest(".shopify-section");
    this._productList = section.querySelector("product-list");
    this._pagination = section.querySelector(".collection__pagination");

    const nextLink = this._pagination ? this._pagination.querySelector('a[rel="next"]') : null;

    if (!this._productList || !nextLink) {
      return;
    }

    this._nextUrl = new URL(nextLink.getAttribute("href"), location.origin);
    this._loading = false;
    this._restoring = false;
    this._loadedPages = 1;
    this._saveTimer = null;

    this._pagination.hidden = true;
    this.hidden = false;

    this._loadObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this._onSentinel();
        }
      },
      { rootMargin: "800px 0px" }
    );

    this._onScroll = () => this._saveState();
    this._onPageHide = () => this._saveState(true);

    // Rückkehr aus dem bfcache: der Browser stellt DOM und Position exakt wieder her, und
    // connectedCallback läuft dabei nicht erneut — der Sentinel steht aber weiterhin im
    // Vorlade-Rand und würde sofort weiterladen. Das Dokument wüchse dann unter der
    // Kundschaft weiter, obwohl sie noch gar nicht gescrollt hat. Also erst wieder laden,
    // wenn sie es durch eigenes Scrollen anfordert.
    this._onPageShow = (event) => {
      if (event.persisted) {
        this._pause();
      }
    };

    window.addEventListener("scroll", this._onScroll, { passive: true });
    window.addEventListener("pagehide", this._onPageHide);
    window.addEventListener("pageshow", this._onPageShow);

    // Restore first, then arm the observer — otherwise the sentinel (still near the top of a
    // short page) would fire its own loads in parallel and interleave the pages.
    this._restore()
      .catch(() => {})
      .then(() => this._arm());
  }

  _arm() {
    if (this._loadObserver && this._nextUrl) {
      this._loadObserver.observe(this);
    }
  }

  // Nachladen stilllegen, bis die Kundschaft selbst scrollt.
  _pause() {
    if (!this._loadObserver) {
      return;
    }

    this._loadObserver.unobserve(this);
    window.addEventListener("scroll", () => this._arm(), { passive: true, once: true });
  }

  _teardown() {
    if (this._loadObserver) {
      this._loadObserver.disconnect();
    }

    if (this._onScroll) {
      window.removeEventListener("scroll", this._onScroll);
      window.removeEventListener("pagehide", this._onPageHide);
      window.removeEventListener("pageshow", this._onPageShow);
    }

    if (this._saveTimer) {
      clearTimeout(this._saveTimer);
      this._saveTimer = null;
    }
  }

  // Fall back to the server-rendered pagination: the visitor keeps a working, if manual, way
  // through the collection.
  _degrade() {
    this._teardown();
    this._nextUrl = null;

    if (this._pagination) {
      this._pagination.hidden = false;
    }

    this.hidden = true;
  }

  // ------------------------------------------------------------------------------------------
  // State in history.state (the address bar is never modified)
  // ------------------------------------------------------------------------------------------

  // The key pins the state to one list. Filters and sort live in the query string, so a
  // different filter combination yields a different key and never restores foreign pages.
  get _stateKey() {
    return location.pathname + location.search;
  }

  _saveState(immediate) {
    if (this._restoring) {
      return;
    }

    if (immediate) {
      if (this._saveTimer) {
        clearTimeout(this._saveTimer);
        this._saveTimer = null;
      }

      this._writeState();
      return;
    }

    if (this._saveTimer) {
      return;
    }

    this._saveTimer = setTimeout(() => {
      this._saveTimer = null;
      this._writeState();
    }, SAVE_THROTTLE_MS);
  }

  _writeState() {
    try {
      const state = Object.assign({}, history.state);

      state[STATE_KEY] = {
        key: this._stateKey,
        pages: this._loadedPages,
        scrollY: Math.round(window.scrollY)
      };

      // No URL argument: replaceState keeps the current address untouched.
      history.replaceState(state, "");
    } catch (error) {
      // A blocked history write only costs the restore, not the page.
    }
  }

  _readState() {
    const saved = history.state ? history.state[STATE_KEY] : null;

    if (!saved || saved.key !== this._stateKey) {
      return null;
    }

    return saved;
  }

  // ------------------------------------------------------------------------------------------
  // Restore
  // ------------------------------------------------------------------------------------------

  async _restore() {
    const saved = this._readState();

    if (!saved) {
      return;
    }

    // bfcache hit: the DOM already carries every page, so only the counter needs adopting.
    if (this._productList.childElementCount > 1 && saved.pages > 1 && this._alreadyRestored(saved)) {
      this._loadedPages = saved.pages;
      return;
    }

    if (saved.pages <= 1 && !saved.scrollY) {
      return;
    }

    this._restoring = true;

    // We place the visitor ourselves; the browser's own guess would land on the short page.
    const previousRestoration = history.scrollRestoration;

    try {
      history.scrollRestoration = "manual";
    } catch (error) {
      // Not supported — the re-scrolls below still get us close enough.
    }

    const targetPages = Math.min(saved.pages, MAX_RESTORE_PAGES);
    const deadline = Date.now() + RESTORE_BUDGET_MS;

    // Re-scrolling after every batch keeps the viewport creeping towards the saved offset
    // instead of jumping there once at the end (the browser clamps to the current document
    // height, so a single early call would land short).
    window.scrollTo(0, saved.scrollY);

    while (this._loadedPages < targetPages && this._nextUrl && Date.now() < deadline) {
      const ok = await this._loadBatch(targetPages);

      if (!ok) {
        break;
      }

      window.scrollTo(0, saved.scrollY);
    }

    window.scrollTo(0, saved.scrollY);
    this._restoring = false;

    try {
      history.scrollRestoration = previousRestoration || "auto";
    } catch (error) {
      // See above.
    }

    this._saveState(true);
  }

  // A page served fresh from the server carries exactly one page worth of items; anything more
  // means the DOM came back intact (bfcache).
  _alreadyRestored(saved) {
    const perPage = Number(this.getAttribute("per-page")) || 0;

    if (!perPage) {
      return false;
    }

    return this._productList.childElementCount > perPage;
  }

  // ------------------------------------------------------------------------------------------
  // Loading
  // ------------------------------------------------------------------------------------------

  // Fetches up to RESTORE_BATCH consecutive pages at once and appends them in order.
  // Only used while restoring; normal scrolling stays one page at a time.
  //
  // The next page's URL is only known from the previous page's markup, so the batch is built
  // by walking the ?page=N numbers forward from the current one — the same URLs the pagination
  // would produce. A page that comes back without products ends the batch.
  async _loadBatch(targetPages) {
    const urls = [];
    const cursor = new URL(this._nextUrl);
    const firstPage = Number(cursor.searchParams.get("page")) || 2;

    for (let i = 0; i < RESTORE_BATCH && this._loadedPages + i < targetPages; i++) {
      const url = new URL(cursor);
      url.searchParams.set("page", String(firstPage + i));
      urls.push(url);
    }

    if (!urls.length) {
      return false;
    }

    this.setAttribute("loading", "");

    try {
      const documents = await Promise.all(urls.map((url) => this._fetchPage(url)));
      let appended = 0;

      for (let i = 0; i < documents.length; i++) {
        const fetched = documents[i];
        const items = Array.from(fetched.querySelectorAll("product-list > *"), (item) => document.importNode(item, true));

        if (!items.length) {
          this._nextUrl = null;
          break;
        }

        this._productList.append(...items);
        this._loadedPages += 1;
        appended += 1;

        const nextLink = fetched.querySelector('.collection__pagination a[rel="next"]');
        this._nextUrl = nextLink ? new URL(nextLink.getAttribute("href"), location.origin) : null;

        if (!this._nextUrl) {
          break;
        }
      }

      if (!this._nextUrl) {
        this._loadObserver.disconnect();
        this.hidden = true;
      }

      return appended > 0;
    } catch (error) {
      // Restoring failed halfway: keep what is already on screen and let normal scrolling
      // take over from here instead of blanking the list.
      return false;
    } finally {
      this.removeAttribute("loading");
    }
  }

  async _fetchPage(url) {
    const fetchUrl = new URL(url);
    fetchUrl.searchParams.set("section_id", this.getAttribute("section-id"));

    const response = await fetch(fetchUrl.toString());

    if (!response.ok) {
      throw new Error(`Section request failed (${response.status})`);
    }

    return new DOMParser().parseFromString(await response.text(), "text/html");
  }

  _onSentinel() {
    if (this._restoring) {
      return;
    }

    this._loadNext();
  }

  async _loadNext() {
    if (this._loading || !this._nextUrl) {
      return false;
    }

    this._loading = true;
    this.setAttribute("loading", "");

    try {
      const fetched = await this._fetchPage(this._nextUrl);
      const items = Array.from(fetched.querySelectorAll("product-list > *"), (item) => document.importNode(item, true));

      this._productList.append(...items);
      this._loadedPages += 1;

      const nextLink = fetched.querySelector('.collection__pagination a[rel="next"]');
      this._nextUrl = nextLink ? new URL(nextLink.getAttribute("href"), location.origin) : null;
      this._loading = false;

      if (this._nextUrl) {
        // Re-observing emits a fresh entry, so loading continues immediately when the sentinel
        // is still within the prefetch margin.
        if (!this._restoring) {
          this._loadObserver.unobserve(this);
          this._loadObserver.observe(this);
        }
      } else {
        this._loadObserver.disconnect();
        this.hidden = true;
      }

      this._saveState();
      return true;
    } catch (error) {
      // Network or section error: restore the regular pagination as fallback.
      this._loading = false;
      this._degrade();
      return false;
    } finally {
      this.removeAttribute("loading");
    }
  }
}

if (!window.customElements.get("infinite-scroll")) {
  window.customElements.define("infinite-scroll", InfiniteScroll);
}
